import { c as _c } from "react/compiler-runtime";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { MenuItem, PropsWithClassName } from "@/types/ui";
import { ChevronRight } from "lucide-react";
import React, { ReactNode } from "react";
import { AppSidebarItem } from "@components/app/app-sidebar/app-sidebar-item";
import { SidebarGroup, SidebarGroupLabel, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarMenuSub, useSidebar } from "@components/ui/sidebar";
import { cn } from "@lib/utils";

/**
 * Component representing a group of items in the application sidebar.
 *
 * @param {Object} props - The properties for the AppSidebarGroup component.
 * @param {string} props.title - The title of the sidebar group.
 * @param {MenuItem[]} props.items - An array of menu items to be displayed within the group.
 * @param {boolean} [props.hiddenOnCollapsible] - Optional flag to hide the group when in collapsible mode.
 * @returns {React.ReactNode} The rendered sidebar group component.
 */
export const AppSidebarGroup = t0 => {
  const $ = _c(17);
  const {
    title,
    items,
    hiddenOnCollapsible,
    className
  } = t0;
  const {
    isMobile,
    state
  } = useSidebar();
  let t1;
  if ($[0] !== isMobile || $[1] !== state) {
    t1 = item => {
      if (item.isFullButton || !item.items || item.items?.length === 0) {
        return <AppSidebarItem key={item.label} item={item} isMobile={isMobile} />;
      }
      if (item.items && item.items.length > 0 && state === "expanded") {
        return <Collapsible key={item.label} asChild={true} defaultOpen={item.isActive} className="group/collapsible"><SidebarMenuItem><CollapsibleTrigger asChild={true}><SidebarMenuButton tooltip={item.label} isActive={item.isActive} disabled={item.isDisabled}>{item.icon && <item.icon />}<span>{item.label}</span><ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" /></SidebarMenuButton></CollapsibleTrigger><CollapsibleContent><SidebarMenuSub>{item.items?.map(subItem => <AppSidebarItem key={subItem.label} item={subItem} isMobile={isMobile} isSubItem={true} />)}</SidebarMenuSub></CollapsibleContent></SidebarMenuItem></Collapsible>;
      }
      if (item.items && item.items.length > 0 && state === "collapsed") {
        return item.items?.map(subItem_0 => <AppSidebarItem key={subItem_0.label} item={subItem_0} isMobile={isMobile} />);
      }
      return <AppSidebarItem key={item.label} item={item} isMobile={isMobile} />;
    };
    $[0] = isMobile;
    $[1] = state;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const renderItems = t1;
  if (items.length === 0) {
    return null;
  }
  let t2;
  if ($[3] !== className || $[4] !== hiddenOnCollapsible) {
    t2 = cn({
      "group-data-[collapsible=icon]:hidden": hiddenOnCollapsible
    }, className);
    $[3] = className;
    $[4] = hiddenOnCollapsible;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== title) {
    t3 = title && <SidebarGroupLabel>{title}</SidebarGroupLabel>;
    $[6] = title;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== items || $[9] !== renderItems) {
    t4 = items.map(renderItems);
    $[8] = items;
    $[9] = renderItems;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t4) {
    t5 = <SidebarMenu>{t4}</SidebarMenu>;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t2 || $[14] !== t3 || $[15] !== t5) {
    t6 = <SidebarGroup className={t2}>{t3}{t5}</SidebarGroup>;
    $[13] = t2;
    $[14] = t3;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
};